import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ChirpPropShapes from 'javascripts/shared/chirp_prop_shapes';

import { bubEvents } from 'javascripts/shared/events/bub_events';
import { graphQLHelpers } from 'javascripts/shared/graphql/helpers';
import { graphQLQueries } from 'javascripts/shared/graphql/queries';

import { BookCardCondensed } from 'javascripts/components/book_card_condensed';
import { BookCardCondensedPlaceholder } from 'javascripts/components/placeholders/book_card_condensed_placeholder';
import { CarouselWithGraphQL } from 'javascripts/components/carousel_with_graphql';
import { ChirpButton } from 'javascripts/components/chirp_button';
import { CoverImage } from 'javascripts/components/cover_image';
import { Modal } from 'javascripts/components/modal';

import styles from 'stylesheets/components/deals/added_to_cart_modal.module.scss';

const RELATED_BOOKS_COUNT = 4;

export function AddedToCartModal(props) {
  const [carouselTitle, setCarouselTitle] = useState('');

  function renderCarouselItems(bookList, trackingCategory, carouselPosition) {
    return bookList.map((book, index) => (
      <div key={`${trackingCategory} ${book.id}`}>
        <BookCardCondensed
          audiobook={book}
          trackingCategory={trackingCategory}
          trackingProperties={{
            purchased_audiobook_id: props.audiobook.id,
            carousel_horizontal_position: bubEvents.trackingIndex(index),
            carousel_vertical_position: carouselPosition,
          }}
          showPrices
        />
      </div>
    ));
  }

  function onDataLoad(graphQLResponse) {
    if (graphQLResponse.audiobook.relatedAudiobooks.length > 0) {
      setCarouselTitle('You May Also Like');
    } else {
      setCarouselTitle('Trending Deals');
    }
  }

  function renderRecommendedBooksResponse(graphQLResponse, trackingCategory) {
    if (graphQLResponse.loading) {
      return [];
    }

    if (graphQLResponse.audiobook.relatedAudiobooks.length > 0) {
      return renderCarouselItems(graphQLResponse.audiobook.relatedAudiobooks, trackingCategory, 1);
    }

    const items = graphQLHelpers
      .reshapePromotionsResponse(graphQLResponse.dealsPagePromotionsDataWithTags.objects)
      .filter(audiobook => audiobook.id !== props.audiobook.id)
      .slice(0, RELATED_BOOKS_COUNT);
    return renderCarouselItems(items, trackingCategory, 1);
  }

  function renderRecommendedBooksCarousel() {
    const trackingCategory = `Added to Cart Modal ${carouselTitle} Carousel`;
    return (
      <div className={styles.recommendedAudiobooks}>
        <CarouselWithGraphQL
          query={graphQLQueries.addToShoppingCartRecommendedAudiobooks}
          queryParams={{
            id: props.audiobook.id,
            relatedCount: RELATED_BOOKS_COUNT,
            dealsCount: RELATED_BOOKS_COUNT + 1,
          }}
          renderResponseFunction={renderRecommendedBooksResponse}
          title={<div className={styles.carouselTitle}>{carouselTitle}</div>}
          trackingCategory={trackingCategory}
          slideSize={RELATED_BOOKS_COUNT}
          hideArrows
          hideEmptyCarousel
          onDataLoad={onDataLoad}
          placeholderItem={<BookCardCondensedPlaceholder />}
        />
      </div>
    );
  }

  function renderListingPrice() {
    if (props.audiobook.currentProduct.isFreeListing) {
      return <div className={styles.freeListing}>Our Price:</div>;
    }

    if (!props.audiobook.currentProduct.showListingPrice) {
      return null;
    }

    return (
      <div className={styles.retailPrice} aria-hidden='true'>
        {props.audiobook.currentProduct.listingPrice}
      </div>
    );
  }

  function renderPricing() {
    if (_.isEmpty(props.audiobook.currentProduct)) {
      return null;
    }
    return (
      <div className={styles.priceInfo}>
        {renderListingPrice()}
        <div className={styles.discountedPrice} aria-label='Discount price'>
          {props.audiobook.currentProduct.discountPrice}
        </div>
      </div>
    );
  }

  function renderModalContent() {
    return (
      <div className={styles.addedToCartModal}>
        <div className={styles.modalHeader}>ADDED TO YOUR CART</div>
        <div className={styles.bookWrapper}>
          <div className={styles.coverImageContainer}>
            <CoverImage
              bookAuthor={props.audiobook.displayAuthors}
              bookTitle={props.audiobook.displayTitle}
              coverUrl={props.audiobook.coverUrl}
              defer={false}
            />
          </div>
          <div className={styles.bookInfo}>
            <div className={styles.bookTitle}>{props.audiobook.displayTitle}</div>
            <div className={styles.authorInfo}>by {props.audiobook.displayAuthors}</div>
            {renderPricing()}
          </div>
        </div>
        <div>
          <ChirpButton
            class={styles.keepShoppingButton}
            label='Keep Shopping'
            onClick={props.onRequestClose}
            fullWidth
          />
          <ChirpButton displayStyle='yellow' displayType='button' href='/purchases/new' label='Check Out' fullWidth />
        </div>
        {renderRecommendedBooksCarousel()}
      </div>
    );
  }

  return (
    <Modal appSelector='#add-to-cart-button' isOpen={props.isOpen} onRequestClose={props.onRequestClose}>
      {renderModalContent()}
    </Modal>
  );
}

AddedToCartModal.propTypes = {
  audiobook: ChirpPropShapes.audiobook.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func,
};

AddedToCartModal.defaultProps = {
  isOpen: false,
};
